/**
 * WYSIWYG Utilities Module
 * Utility functions for file validation and textarea synchronization.
 */

import { notifier } from "../core/notifier/notifier";
import { translator } from "../translation/translator";
import { Patterns } from "../constants/patterns";
import { SNSLinksUtils } from "../sns/links/sns-links-utils";
import {
    extractInstagramPermalinkFromOfficialEmbedHtml,
    normalizeInstagramPermalinkForEmbed,
} from "../sns/extractor/instagram-embed-extractor";
import { convertInstagramOfficialEmbedHtmlToMinimalEmbedHtml } from "../sns/extractor/instagram-embed-minifier";
import {
    extractTikTokVideoUrlFromOfficialEmbedHtml,
    normalizeTikTokVideoUrl,
} from "../sns/extractor/tiktok-embed-extractor";
import { extractFacebookPostUrlFromEmbedHtml } from "../sns/extractor/facebook-embed-extractor";
import { extractXStatusUrlFromOfficialEmbedHtml, normalizeXStatusUrl } from "../sns/extractor/x-embed-extractor";
import { ImageExif } from "../util/media/image/image-exif";
import { WysiwygForm } from "./wysiwyg-form";
import { WysiwygRender } from "./wysiwyg-render";
import { WysiwygUploadPlaceholderBlotUtils } from "./wysiwyg-upload-placeholder-blot-utils";
import type * as z from "zod";
import { UploadMedia201Response, UploadMediaBody } from "../api/generated/posts.zod";

interface ImagePreprocessOptions {
    maxDimension?: number;
    jpegQuality?: number;
}

interface QuillRangeLike {
    index: number;
}

interface QuillBlotLike {
    length?: () => number;
}

interface QuillStaticLike {
    find?(node: Element): QuillBlotLike | null | undefined;
}

interface QuillClipboardLike {
    dangerouslyPasteHTML(index: number, html: string, source?: string): void;
}

interface QuillLike {
    root: HTMLElement;
    clipboard: QuillClipboardLike;
    constructor?: QuillStaticLike;
    getSelection(focus?: boolean): QuillRangeLike | null;
    getLength(): number;
    setSelection(index: number, length: number, source?: string): void;
    insertEmbed(index: number, type: string, value: string | Record<string, unknown>, source?: string): void;
    deleteText(index: number, length: number, source?: string): void;
    update(source?: string): void;
    getIndex(blot: QuillBlotLike): number;
}

interface WysiwygRenderLike {
    trackContentImage(filename: string): void;
    updateContentImagesList(): void;
}

type UploadResult = z.infer<typeof UploadMedia201Response>;

interface WysiwygApiLike {
    uploadMedia(
        file: File,
        type: z.infer<typeof UploadMediaBody>["type"],
        boardId: z.infer<typeof UploadMediaBody>["board_id"],
        options?: Record<string, unknown>,
    ): Promise<UploadResult>;
}

type UploadCallback = (file: File, type: "image" | "video") => Promise<void> | void;
type DeleteCallback = ((filename: string) => Promise<void> | void) | null | undefined;

interface ExtractAndStoreResult {
    links: string[];
    contentModified: boolean;
    cleanedContent: string | null;
}

class WysiwygUtils {
    public static async preprocessImageFileForUpload(file: File, options: ImagePreprocessOptions = {}): Promise<File> {
        const maxDimension = typeof options.maxDimension === "number" ? options.maxDimension : 1200;
        const jpegQuality = typeof options.jpegQuality === "number" ? options.jpegQuality : 0.85;

        if (!file || !file.type || !file.type.startsWith("image/")) return file;
        if (file.type === "image/gif") return file;

        const isJpeg = file.type === "image/jpeg" || file.type === "image/jpg";
        const isPng = file.type === "image/png";
        if (!isJpeg && !isPng) return file;

        try {
            const buffer = await file.arrayBuffer();
            const orientation = isJpeg ? ImageExif.getExifOrientation(buffer) || 1 : 1;

            const objectUrl = URL.createObjectURL(file);
            try {
                const img = new Image();
                img.decoding = "async";
                img.src = objectUrl;
                if (img.decode) {
                    await img.decode();
                } else {
                    await new Promise((resolve, reject) => {
                        img.onload = () => resolve(null);
                        img.onerror = () => reject(new Error("Image load failed"));
                    });
                }

                const srcW = img.naturalWidth || img.width;
                const srcH = img.naturalHeight || img.height;
                if (!srcW || !srcH) return file;

                const pixelCount = srcW * srcH;
                if (pixelCount > 50_000_000) {
                    return file;
                }

                const swapsWH = orientation >= 5 && orientation <= 8;
                const orientedW = swapsWH ? srcH : srcW;
                const orientedH = swapsWH ? srcW : srcH;

                const scale = Math.min(1, maxDimension / Math.max(orientedW, orientedH));
                const targetW = Math.max(1, Math.round(orientedW * scale));
                const targetH = Math.max(1, Math.round(orientedH * scale));

                if (scale === 1 && orientation === 1) {
                    return file;
                }

                const canvas = document.createElement("canvas");
                canvas.width = targetW;
                canvas.height = targetH;
                const ctx = canvas.getContext("2d");
                if (!ctx) return file;

                ctx.save();
                switch (orientation) {
                    case 2:
                        ctx.translate(targetW, 0);
                        ctx.scale(-1, 1);
                        break;
                    case 3:
                        ctx.translate(targetW, targetH);
                        ctx.rotate(Math.PI);
                        break;
                    case 4:
                        ctx.translate(0, targetH);
                        ctx.scale(1, -1);
                        break;
                    case 5:
                        ctx.rotate(0.5 * Math.PI);
                        ctx.scale(1, -1);
                        ctx.translate(0, -targetH);
                        break;
                    case 6:
                        ctx.rotate(0.5 * Math.PI);
                        ctx.translate(0, -targetH);
                        break;
                    case 7:
                        ctx.rotate(0.5 * Math.PI);
                        ctx.translate(targetW, -targetH);
                        ctx.scale(-1, 1);
                        break;
                    case 8:
                        ctx.rotate(-0.5 * Math.PI);
                        ctx.translate(-targetW, 0);
                        break;
                    default:
                        break;
                }

                const drawW = swapsWH ? targetH : targetW;
                const drawH = swapsWH ? targetW : targetH;
                ctx.drawImage(img, 0, 0, drawW, drawH);
                ctx.restore();

                const outType = isJpeg ? "image/jpeg" : "image/png";
                const blob = await new Promise<Blob | null>((resolve) => {
                    canvas.toBlob((value) => resolve(value), outType, isJpeg ? jpegQuality : undefined);
                });

                if (!(blob instanceof Blob) || blob.size <= 0) return file;

                return new File([blob], file.name, { type: outType, lastModified: Date.now() });
            } finally {
                URL.revokeObjectURL(objectUrl);
            }
        } catch (_error) {
            return file;
        }
    }

    public static detectSnsUrlsFromText(text: string): string[] {
        if (!text) return [];

        const decoded = Patterns.decodeHtmlEntities(String(text));

        const links: string[] = [];

        if (decoded.includes("instagram-media") || decoded.includes("data-instgrm-permalink")) {
            const minimal = convertInstagramOfficialEmbedHtmlToMinimalEmbedHtml(decoded);
            const extracted = extractInstagramPermalinkFromOfficialEmbedHtml(minimal || decoded);
            const normalized = extracted ? normalizeInstagramPermalinkForEmbed(extracted) : null;
            if (normalized) links.push(normalized);
            else if (extracted) links.push(extracted);
        }

        if (
            decoded.includes("tiktok-embed") ||
            decoded.includes("tiktok.com/embed.js") ||
            (decoded.includes("tiktok.com/") && decoded.includes("cite="))
        ) {
            const extracted = extractTikTokVideoUrlFromOfficialEmbedHtml(decoded);
            const normalized = extracted ? normalizeTikTokVideoUrl(extracted) : null;
            if (normalized) links.push(normalized);
            else if (extracted) links.push(extracted);
        }

        if (
            decoded.includes("facebook.com/plugins/") ||
            decoded.includes("<iframe") ||
            decoded.includes("&lt;iframe")
        ) {
            const extracted = extractFacebookPostUrlFromEmbedHtml(decoded);
            if (extracted) links.push(extracted);
        }

        if (
            decoded.includes("twitter-tweet") ||
            decoded.includes("platform.twitter.com/widgets.js") ||
            decoded.includes("twitter.com/") ||
            decoded.includes("x.com/")
        ) {
            const extracted = extractXStatusUrlFromOfficialEmbedHtml(decoded);
            const normalized = extracted ? normalizeXStatusUrl(extracted) : null;
            if (normalized) links.push(normalized);
            else if (extracted) links.push(extracted);
        }

        const facebook = Patterns.match(decoded, Patterns.FACEBOOK_URL) || [];
        const youtube = Patterns.match(decoded, Patterns.YOUTUBE_URL) || [];
        const youtubeShort = Patterns.match(decoded, Patterns.YOUTUBE_SHORT_URL) || [];
        const instagram = Patterns.match(decoded, Patterns.INSTAGRAM_URL) || [];
        const twitter = Patterns.match(decoded, Patterns.TWITTER_URL) || [];

        links.push(...facebook, ...youtube, ...youtubeShort, ...instagram, ...twitter);

        const tiktokVideoUrls = Patterns.match(decoded, Patterns.SNS_PLATFORMS.tiktok.regex) || [];
        tiktokVideoUrls.forEach((value) => {
            const normalized = normalizeTikTokVideoUrl(value);
            if (normalized) links.push(normalized);
        });

        const anyUrls = (Patterns.match(decoded, Patterns.URL) || []) as string[];
        anyUrls.forEach((value) => {
            if (!value.includes("tiktok.com")) return;
            const normalized = normalizeTikTokVideoUrl(value);
            if (normalized) links.push(normalized);
        });

        return SNSLinksUtils.normalizeSnsLinks(links);
    }

    public static mergeSnsLinksIntoEditableList(
        textarea: HTMLTextAreaElement | null | undefined,
        incomingLinks: string[],
    ): string[] {
        const form = textarea?.form;
        if (!form) return [];

        const snsLinksList = WysiwygForm.getSnsLinksList(form);
        if (!(snsLinksList instanceof HTMLElement)) return [];

        const currentInputs = WysiwygForm.getSnsLinkInputs(snsLinksList)
            .map((element) => element.value.trim())
            .filter(Boolean);

        const merged = SNSLinksUtils.normalizeSnsLinks([...(currentInputs || []), ...(incomingLinks || [])]);
        WysiwygRender.renderSnsLinksList(snsLinksList, merged);

        WysiwygForm.removeHiddenSnsLinkInputs(form);

        const inputEls = WysiwygForm.getSnsLinkInputs(snsLinksList);
        inputEls.forEach((element) => {
            try {
                element.dispatchEvent(new Event("input", { bubbles: true }));
            } catch (_error) {
                // ignore
            }
        });

        return merged;
    }

    public static hasImageFiles(files: FileList): boolean {
        for (let index = 0; index < files.length; index += 1) {
            if (files[index].type.indexOf("image/") === 0) {
                return true;
            }
        }
        return false;
    }

    public static hasVideoFiles(files: FileList): boolean {
        for (let index = 0; index < files.length; index += 1) {
            if (files[index].type.indexOf("video/") === 0) {
                return true;
            }
        }
        return false;
    }

    public static syncTextarea(quill: QuillLike, textarea: HTMLTextAreaElement | null | undefined): void {
        if (textarea && quill) {
            textarea.value = quill.root.innerHTML;
            WysiwygUtils.dispatchTextareaEvent(textarea, "input");
        }
    }

    public static dispatchTextareaEvent(textarea: HTMLTextAreaElement, eventType: string): void {
        try {
            const event = new Event(eventType, { bubbles: true });
            textarea.dispatchEvent(event);
        } catch (error) {
            console.error(error);
            const evt = document.createEvent("Event");
            evt.initEvent(eventType, true, true);
            textarea.dispatchEvent(evt);
        }
    }

    public static async uploadMedia(
        file: File,
        type: "image" | "video",
        boardId: string,
        quill: QuillLike,
        render: WysiwygRenderLike,
        _onDelete: DeleteCallback,
        textarea: HTMLTextAreaElement,
        api: WysiwygApiLike,
    ): Promise<void> {
        const uploadId = `upl_${Date.now()}_${Math.random().toString(36).slice(2, 9)}`;
        let placeholderInserted = false;

        try {
            try {
                const range = quill.getSelection(true);
                const index = range ? range.index : quill.getLength();

                quill.insertEmbed(index, WysiwygUploadPlaceholderBlotUtils.formatName, { uploadId }, "user");
                quill.setSelection(index + 1, 0, "user");
                placeholderInserted = true;
                translator?.updateUITranslations();
                WysiwygUtils.syncTextarea(quill, textarea);
            } catch (error) {
                console.warn("Failed to insert upload placeholder", error);
            }

            const result = await api.uploadMedia(file, type, boardId, { noSpinner: true });

            if (result.success === 1 && result.url) {
                const { url, filename } = result;
                const embedType = type === "video" ? "video" : "image";
                const placeholderEl = WysiwygForm.getUploadPlaceholder(quill.root, uploadId);

                if (placeholderEl) {
                    const quillCtor = quill?.constructor as QuillStaticLike | undefined;
                    const blot = quillCtor?.find ? quillCtor.find(placeholderEl) : null;

                    if (blot) {
                        const index = quill.getIndex(blot);
                        const length = typeof blot.length === "function" ? blot.length() : 1;
                        quill.deleteText(index, length, "user");
                        quill.insertEmbed(index, embedType, url, "user");
                        quill.setSelection(index + 1, 0, "user");
                    } else {
                        placeholderEl.remove();
                        quill.update("user");
                        const range = quill.getSelection(true);
                        const index = range ? range.index : quill.getLength();
                        quill.insertEmbed(index, embedType, url, "user");
                        quill.setSelection(index + 1, 0, "user");
                    }
                } else {
                    const range = quill.getSelection(true);
                    const index = range ? range.index : quill.getLength();
                    quill.insertEmbed(index, embedType, url, "user");
                    quill.setSelection(index + 1, 0, "user");
                }

                if (type === "image" && filename) {
                    render.trackContentImage(filename);
                    render.updateContentImagesList();
                }

                WysiwygUtils.syncTextarea(quill, textarea);

                const messageKey = type === "image" ? "wysiwyg_image_uploaded" : "wysiwyg_video_uploaded";
                const message =
                    translator?.getTranslation(messageKey) ||
                    (type === "image" ? "Image uploaded successfully" : "Video uploaded successfully");
                notifier.success(message);
            } else {
                const errorMessage = "Media upload failed";
                notifier.error(errorMessage);
                throw new Error(errorMessage);
            }
        } catch (error) {
            console.error("Upload error:", error);

            if (placeholderInserted) {
                try {
                    const placeholderEl = WysiwygForm.getUploadPlaceholder(quill.root, uploadId);
                    if (placeholderEl) {
                        const quillCtor = quill?.constructor as QuillStaticLike | undefined;
                        const blot = quillCtor?.find ? quillCtor.find(placeholderEl) : null;
                        if (blot) {
                            const index = quill.getIndex(blot);
                            const length = typeof blot.length === "function" ? blot.length() : 1;
                            quill.deleteText(index, length, "user");
                        } else {
                            placeholderEl.remove();
                            quill.update("user");
                        }
                        WysiwygUtils.syncTextarea(quill, textarea);
                    }
                } catch (placeholderError) {
                    console.warn("Failed to remove upload placeholder", placeholderError);
                }
            }

            if (error instanceof Error && error.message) {
                notifier.error(error.message);
            }
        }
    }

    public static async processImageFiles(files: FileList, uploadCallback: UploadCallback): Promise<void> {
        for (let index = 0; index < files.length; index += 1) {
            const file = files[index];
            if (file && file.type && file.type.indexOf("image/") === 0) {
                const processed = await WysiwygUtils.preprocessImageFileForUpload(file);
                await uploadCallback(processed, "image");
            }
        }
    }

    public static async processVideoFiles(files: FileList, uploadCallback: UploadCallback): Promise<void> {
        for (let index = 0; index < files.length; index += 1) {
            const file = files[index];
            if (file && file.type && file.type.startsWith("video/")) {
                await uploadCallback(file, "video");
            }
        }
    }

    public static extractAndStoreSNSLinks(content: string, textarea: HTMLTextAreaElement): ExtractAndStoreResult {
        const snsLinks: string[] = [];
        let contentModified = false;
        let cleanedContent: string | null = null;

        const tempDiv = document.createElement("div");
        tempDiv.innerHTML = content;
        const decodedContent = tempDiv.innerHTML;

        if (decodedContent.includes("<iframe")) {
            try {
                let next = cleanedContent ?? decodedContent;
                next = next.replace(Patterns.reset(Patterns.PARAGRAPH_WITH_IFRAME), "");
                next = next.replace(Patterns.reset(Patterns.IFRAME_TAG), "");
                next = next.replace(Patterns.reset(Patterns.IFRAME_SELF_CLOSING), "");
                next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH), "");
                next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH_SIMPLE), "");
                cleanedContent = next;
                contentModified = true;
            } catch (_error) {
                // ignore
            }
        }

        if (decodedContent.includes("instagram-media") || decodedContent.includes("data-instgrm-permalink")) {
            try {
                const minimal = convertInstagramOfficialEmbedHtmlToMinimalEmbedHtml(decodedContent);
                const extracted = extractInstagramPermalinkFromOfficialEmbedHtml(minimal || decodedContent);
                const normalized = extracted ? normalizeInstagramPermalinkForEmbed(extracted) : null;
                const out = normalized || extracted;
                if (out && typeof out === "string") {
                    snsLinks.push(out);
                    contentModified = true;

                    let next = cleanedContent ?? decodedContent;
                    next = next.replace(
                        /<blockquote[^>]*class=["'][^"']*instagram-media[^"']*["'][\s\S]*?<\/blockquote>/gi,
                        "",
                    );
                    next = next.replace(/<script[^>]*instagram\.com\/embed\.js[^>]*>[\s\S]*?<\/script>/gi, "");
                    next = next.replace(/<script[^>]*platform\.instagram\.com\/[^>]*>[\s\S]*?<\/script>/gi, "");
                    next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH), "");
                    next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH_SIMPLE), "");
                    cleanedContent = next;
                }
            } catch (_error) {
                // ignore
            }
        }

        if (decodedContent.includes("tiktok-embed") || decodedContent.includes("tiktok.com/embed.js")) {
            try {
                const extracted = extractTikTokVideoUrlFromOfficialEmbedHtml(decodedContent);
                const normalized = extracted ? normalizeTikTokVideoUrl(extracted) : null;
                const out = normalized || extracted;
                if (out && typeof out === "string") {
                    snsLinks.push(out);
                    contentModified = true;

                    let next = cleanedContent ?? decodedContent;
                    next = next.replace(
                        /<blockquote[^>]*class=["'][^"']*tiktok-embed[^"']*["'][\s\S]*?<\/blockquote>/gi,
                        "",
                    );
                    next = next.replace(/<script[^>]*tiktok\.com\/embed\.js[^>]*>[\s\S]*?<\/script>/gi, "");
                    next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH), "");
                    next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH_SIMPLE), "");
                    cleanedContent = next;
                }
            } catch (_error) {
                // ignore
            }
        }

        if (
            decodedContent.includes("twitter-tweet") ||
            decodedContent.includes("platform.twitter.com/widgets.js") ||
            decodedContent.includes("twitter.com/") ||
            decodedContent.includes("x.com/")
        ) {
            try {
                const extracted = extractXStatusUrlFromOfficialEmbedHtml(decodedContent);
                const normalized = extracted ? normalizeXStatusUrl(extracted) : null;
                const out = normalized || extracted;
                if (out && typeof out === "string") {
                    snsLinks.push(out);
                    contentModified = true;

                    let next = cleanedContent ?? decodedContent;
                    next = next.replace(
                        /<blockquote[^>]*class=["'][^"']*twitter-tweet[^"']*["'][\s\S]*?<\/blockquote>/gi,
                        "",
                    );
                    next = next.replace(
                        /<script[^>]*platform\.twitter\.com\/widgets\.js[^>]*>[\s\S]*?<\/script>/gi,
                        "",
                    );
                    next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH), "");
                    next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH_SIMPLE), "");
                    cleanedContent = next;
                }
            } catch (_error) {
                // ignore
            }
        }

        const fbIframeBlocks = decodedContent.match(/<iframe[^>]*facebook\.com[^>]*>[\s\S]*?<\/iframe>/gi) || [];
        if (fbIframeBlocks.length > 0) {
            fbIframeBlocks.forEach((iframeHtml) => {
                const url = extractFacebookPostUrlFromEmbedHtml(iframeHtml);
                if (url) {
                    snsLinks.push(url);
                    contentModified = true;
                }
            });

            try {
                let next = decodedContent;
                next = next.replace(Patterns.reset(Patterns.PARAGRAPH_WITH_IFRAME), "");
                next = next.replace(Patterns.reset(Patterns.IFRAME_TAG), "");
                next = next.replace(Patterns.reset(Patterns.IFRAME_SELF_CLOSING), "");
                next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH), "");
                next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH_SIMPLE), "");
                cleanedContent = next;
            } catch (_error) {
                // ignore
            }
        }

        const escapedMatches = Patterns.match(content, Patterns.ESCAPED_IFRAME);

        if (escapedMatches) {
            const decodedEscaped = Patterns.decodeHtmlEntities(content);

            const srcPattern = Patterns.reset(Patterns.IFRAME_SRC);
            let srcMatch: RegExpExecArray | null;
            while ((srcMatch = srcPattern.exec(decodedEscaped)) !== null) {
                const srcUrl = srcMatch[1];

                if (srcUrl.includes("facebook.com/plugins/")) {
                    const hrefMatch = srcUrl.match(Patterns.IFRAME_HREF_QUERY_PARAM);
                    if (hrefMatch && hrefMatch[1]) {
                        const originalUrl = decodeURIComponent(hrefMatch[1]);
                        snsLinks.push(originalUrl);
                    }
                }
            }

            contentModified = true;

            try {
                let next = cleanedContent ?? decodedContent;
                next = next.replace(Patterns.reset(Patterns.PARAGRAPH_WITH_ESCAPED_IFRAME), "");
                next = next.replace(Patterns.reset(Patterns.ESCAPED_IFRAME_STRIP), "");
                next = next.replace(Patterns.reset(Patterns.ESCAPED_IFRAME_STRIP_ALT), "");
                next = next.replace(Patterns.reset(Patterns.ESCAPED_SCRIPT), "");
                next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH), "");
                next = next.replace(Patterns.reset(Patterns.EMPTY_PARAGRAPH_SIMPLE), "");
                cleanedContent = next;
            } catch (_error) {
                // ignore
            }
        }

        const facebookMatches = Patterns.match(decodedContent, Patterns.FACEBOOK_URL);
        if (facebookMatches && fbIframeBlocks.length === 0 && !escapedMatches) {
            snsLinks.push(...facebookMatches);
        }

        const youtubeMatches = Patterns.match(decodedContent, Patterns.YOUTUBE_URL);
        const youtubeShortMatches = Patterns.match(decodedContent, Patterns.YOUTUBE_SHORT_URL);
        if (youtubeMatches) {
            snsLinks.push(...youtubeMatches);
        }
        if (youtubeShortMatches) {
            snsLinks.push(...youtubeShortMatches);
        }

        const instagramMatches = Patterns.match(decodedContent, Patterns.INSTAGRAM_URL);
        if (instagramMatches) {
            snsLinks.push(...instagramMatches);
        }

        const twitterMatches = Patterns.match(decodedContent, Patterns.TWITTER_URL);
        if (twitterMatches) {
            snsLinks.push(...twitterMatches.map((value) => normalizeXStatusUrl(value) || value));
        }

        const uniqueLinks = SNSLinksUtils.normalizeSnsLinks(snsLinks);

        const form = textarea.form;
        if (!form) {
            return { links: uniqueLinks, contentModified, cleanedContent };
        }

        const merged = WysiwygUtils.mergeSnsLinksIntoEditableList(textarea, uniqueLinks);
        if (Array.isArray(merged) && merged.length > 0) {
            return { links: merged, contentModified, cleanedContent };
        }

        WysiwygForm.removeHiddenSnsLinkInputs(form);

        WysiwygRender.renderHiddenSnsLinkInputs(form, uniqueLinks);

        return { links: uniqueLinks, contentModified, cleanedContent };
    }

    public static removeEscapedHTML(quill: QuillLike | null | undefined): void {
        if (!quill) return;

        const content = quill.root.innerHTML;

        if (Patterns.test(content, Patterns.ESCAPED_IFRAME_STRIP) || Patterns.test(content, Patterns.ESCAPED_SCRIPT)) {
            console.warn("Escaped HTML tags detected - removing for security");

            let cleanedContent = content;
            cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.ESCAPED_IFRAME_STRIP), "");
            cleanedContent = cleanedContent.replace(Patterns.reset(Patterns.ESCAPED_SCRIPT), "");

            if (cleanedContent !== content) {
                quill.root.innerHTML = cleanedContent;
            }
        }
    }
}

export { WysiwygUtils };
